/**
 * ScheduleEntryModal — the Schedule page's "+ Schedule" entry point. A thin
 * router, not a rewrite of existing scheduling logic: it just gets the user
 * to "a task" or "an agent/workflow run" and then hands off to the existing
 * AddTaskModal / ScheduleRunModal respectively, pre-filled with whatever
 * date is selected in the calendar sidebar. Only one overlay is ever shown
 * at a time — this component's own chooser UI, or the delegated modal.
 *
 * Deliberately out of scope: setting up new recurring cadences here — that
 * stays in Agent Builder's Run section / Flow scheduler settings. This is
 * only for one-off task/run scheduling, matching what the two modals it
 * delegates to already do.
 */
import React, { useEffect, useState } from 'react';
import { X, CheckSquare, CalendarClock, Search, Bot, Workflow as WorkflowIcon } from 'lucide-react';
import { T2 } from '../dashboard/AppShellV2.tsx';
import { customAgentAPI } from '../../services/api.js';
import AddTaskModal from './AddTaskModal.tsx';
import ScheduleRunModal from '../agentsWorkflows/ScheduleRunModal.jsx';

interface ScheduleEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  onScheduled: () => void;
}

type Step = 'choose' | 'task' | 'pick-target' | 'run';

function toIsoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ScheduleEntryModal({ isOpen, onClose, selectedDate, onScheduled }: ScheduleEntryModalProps) {
  const [step, setStep] = useState<Step>('choose');
  const [targets, setTargets] = useState<any[]>([]);
  const [targetQuery, setTargetQuery] = useState('');
  const [pickedTarget, setPickedTarget] = useState<any | null>(null);
  const [loadingTargets, setLoadingTargets] = useState(false);

  useEffect(() => {
    if (!isOpen) { setStep('choose'); setPickedTarget(null); setTargetQuery(''); }
  }, [isOpen]);

  useEffect(() => {
    if (step !== 'pick-target') return;
    setLoadingTargets(true);
    Promise.allSettled([customAgentAPI.list(), customAgentAPI.listChains()]).then(([agentsRes, chainsRes]) => {
      const agentsVal: any = agentsRes.status === 'fulfilled' ? agentsRes.value : null;
      const chainsVal: any = chainsRes.status === 'fulfilled' ? chainsRes.value : null;
      const agents = Array.isArray(agentsVal) ? agentsVal : (agentsVal?.agents || []);
      const chains = chainsVal?.chains || [];
      setTargets([
        ...agents.map((a: any) => ({ ...a, _targetType: 'agent' })),
        ...chains.map((c: any) => ({ ...c, _targetType: 'chain' })),
      ]);
      setLoadingTargets(false);
    });
  }, [step]);

  if (!isOpen) return null;

  const initialDate = toIsoDate(selectedDate);

  if (step === 'task') {
    return (
      <AddTaskModal
        isOpen
        onClose={onClose}
        onTaskAdded={() => { onScheduled(); onClose(); }}
        mode="task-management"
        initialDueDate={initialDate}
      />
    );
  }

  if (step === 'run' && pickedTarget) {
    return (
      <ScheduleRunModal
        target={pickedTarget}
        targetType={pickedTarget._targetType}
        initialDate={initialDate}
        onClose={onClose}
        onScheduled={() => { onScheduled(); onClose(); }}
      />
    );
  }

  const filteredTargets = targets.filter((t) => {
    const q = targetQuery.toLowerCase();
    return !q || (t.name || '').toLowerCase().includes(q);
  });

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.25)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        zIndex: 201, width: 'min(360px, calc(100vw - 32px))', maxHeight: '70vh', display: 'flex', flexDirection: 'column',
        background: T2.surface, borderRadius: 16, border: `1px solid ${T2.border}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: `1px solid ${T2.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T2.ink }}>
            {step === 'pick-target' ? 'Pick an agent or workflow' : 'Schedule something'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T2.ink3, display: 'flex', padding: 4 }}>
            <X size={15} />
          </button>
        </div>

        {step === 'choose' && (
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={() => setStep('task')}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: `1px solid ${T2.border}`, borderRadius: 12, background: T2.page, cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckSquare size={16} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T2.ink }}>Task</div>
                <div style={{ fontSize: 11.5, color: T2.ink3 }}>Add a task due on this date</div>
              </div>
            </button>
            <button
              onClick={() => setStep('pick-target')}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: `1px solid ${T2.border}`, borderRadius: 12, background: T2.page, cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: T2.accentSoft, color: T2.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CalendarClock size={16} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T2.ink }}>Agent or workflow run</div>
                <div style={{ fontSize: 11.5, color: T2.ink3 }}>Run once, at a specific date &amp; time</div>
              </div>
            </button>
          </div>
        )}

        {step === 'pick-target' && (
          <>
            <div style={{ padding: '10px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${T2.border}`, borderRadius: 9, padding: '7px 12px' }}>
                <Search size={13} color={T2.ink3} />
                <input
                  autoFocus
                  value={targetQuery}
                  onChange={(e) => setTargetQuery(e.target.value)}
                  placeholder="Search agents & workflows…"
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12.5, width: '100%', fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px 14px' }}>
              {loadingTargets ? (
                <div style={{ fontSize: 12, color: T2.ink3, textAlign: 'center', padding: '20px 0' }}>Loading…</div>
              ) : filteredTargets.length === 0 ? (
                <div style={{ fontSize: 12, color: T2.ink3, textAlign: 'center', padding: '20px 0' }}>No agents or workflows found.</div>
              ) : filteredTargets.map((t) => (
                <button
                  key={`${t._targetType}-${t._id || t.id}`}
                  onClick={() => { setPickedTarget(t); setStep('run'); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 8px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T2.page; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: t._targetType === 'chain' ? 'var(--app-primary-soft)' : T2.accentSoft, color: t._targetType === 'chain' ? 'var(--lyra-color-primary)' : T2.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {t._targetType === 'chain' ? <WorkflowIcon size={13} /> : <Bot size={13} />}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T2.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                    <div style={{ fontSize: 10.5, color: T2.ink3 }}>{t._targetType === 'chain' ? 'Workflow' : 'Agent'}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
